import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { CommunicationServiceManagementClient } from "@azure/arm-communication";
import type { EmailFeatureStamp } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { parseContainerAppResourceId } from "../../lib/platform-update.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { readStamp, writeStamp } from "../../lib/platform-stamp.js";
import { resolveAcs, emailFeatureFrom, platformSuffixFromKvUri } from "../../lib/acs-resolve.js";
import { reconcileExecutorEmailEnv, resolveExecutorAgentName, EXECUTOR_AGENT_FALLBACK } from "../../lib/executor-email-env.js";
import { discoverKvUri } from "../../lib/brain-paths.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

/**
 * Turn outbound email on or off for this install, in one command.
 *
 * The state lives in the platform stamp rather than on the executor, because the stamp is
 * what records operator INTENT — and intent, separate from wiring, is what makes the
 * capability durable. Before this existed email hinged on `--enable-email` at deploy time,
 * so the ezra rename redeployed the executor without it and the capability vanished
 * silently for six weeks.
 *
 * It lives under `platform` rather than `azure-exec` for the same reason: the executor is
 * where email LANDS, not where it is owned. A future webapp config page drives this same
 * logic against the same stamp field.
 */
export class PlatformEmailCommand extends M8tCommand {
  static paths = [["platform", "email"]];
  static usage = Command.Usage({
    description: "Turn outbound email (advisor handoffs) on or off for this install.",
    details:
      "`on` resolves the ACS sender (reusing an existing one, provisioning only if there is none), records the decision in the platform stamp, and reconciles the executor's environment. `off` records the decision and strips the executor's email wiring, leaving the ACS resource in place so turning it back on is a flag flip. Idempotent — re-running either direction is safe, and no new agent version is posted when nothing changes.",
    examples: [
      ["Turn outbound email on", "m8t platform email on"],
      ["Turn it off (a shared, public-facing deployment should stay off)", "m8t platform email off"],
    ],
  });

  state = Option.String({ required: true, name: "on|off" });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group, to disambiguate in a multi-deployment subscription.",
  });
  agent = Option.String("--agent", { description: `Executor agent name. Resolved by persona when omitted (usually ${EXECUTOR_AGENT_FALLBACK}).` });
  kvUri = Option.String("--kv-uri", {
    description: "Install's Key Vault URI. Only needed if no ACS is on record yet and one must be provisioned.",
  });
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint, to disambiguate a subscription holding several.",
  });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const wanted = this.state.trim().toLowerCase();
    if (wanted !== "on" && wanted !== "off") {
      throw new LocalCliError({
        code: "USAGE",
        message: `Expected 'on' or 'off', got '${this.state}'.`,
        hint: "Run `m8t platform email on` or `m8t platform email off`.",
      });
    }
    const enable = wanted === "on";

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const onProgress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const ctx = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup } = parseContainerAppResourceId(ctx.containerAppResourceId);
    const subscriptionId = ctx.subscriptionId;
    const credential = new DefaultAzureCredential();

    const stamp = await readStamp({ credential, subscriptionId, resourceGroup });
    if (stamp === null) {
      throw new LocalCliError({
        code: "NO_PLATFORM_STAMP",
        message: `No platform stamp found in ${resourceGroup} — there is nowhere to record the decision.`,
        hint: "This command needs an install written by the current CLI. Check you are pointed at the right deployment with `m8t status`.",
      });
    }

    // Resolve the ACS identity even when turning OFF, if it is already on record: keeping
    // it means re-enabling later is a flag flip rather than another discovery pass.
    let feature: EmailFeatureStamp;
    if (enable) {
      // Resolved LAZILY and never fatally: the suffix is only used on the provisioning leg,
      // and `discoverKvUri` THROWS when AZURE_KEYVAULT_URI is unset. Calling it eagerly made
      // the advertised one-command flip fail in any shell without that variable — including
      // on an install whose stamp already records the ACS identity, where no suffix is needed
      // at all.
      const suffix = this.resolveSuffix();
      const resolution = await resolveAcs({
        client: new CommunicationServiceManagementClient(credential, subscriptionId),
        stamp,
        subscriptionId,
        resourceGroup,
        suffix,
        dataLocation: "United States",
        onProgress,
      });
      onProgress?.(`sender: ${resolution.acs.sender} (${resolution.source}).`);
      feature = emailFeatureFrom(resolution.acs, true);
    } else {
      const prior = stamp.features?.email;
      feature = { enabled: false, acsEndpoint: prior?.acsEndpoint ?? "", acsSender: prior?.acsSender ?? "" };
    }

    await writeStamp({
      credential,
      subscriptionId,
      resourceGroup,
      stamp: { ...stamp, features: { ...stamp.features, email: feature } },
      onProgress,
    });

    // Reconcile the live executor so the flip takes effect now rather than at the next
    // converge. A missing executor is normal (an install need not have one) — report it
    // instead of failing.
    // `--endpoint` matters more here than it looks: this subscription holds several Foundry
    // projects INCLUDING the public-facing prod one, and this command writes to whichever it
    // resolves. Non-interactive mode refuses to guess (FOUNDRY_PROJECT_MULTIPLE) rather than
    // silently reconciling the wrong install's executor.
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: mode !== "json",
      ...(typeof this.endpoint === "string" && this.endpoint.length > 0 ? { endpoint: this.endpoint } : {}),
      ...(typeof this.resourceGroup === "string" && this.resourceGroup.length > 0
        ? { resourceGroup: this.resourceGroup }
        : {}),
    });
    // Resolved by PERSONA, not by name: the instance name is operator-chosen and was renamed
    // once already, which is what caused this outage in the first place.
    const { agentName, provenance } = await resolveExecutorAgentName({
      credential,
      projectEndpoint: project.endpoint,
      ...(typeof this.agent === "string" && this.agent.length > 0 ? { override: this.agent } : {}),
    });
    const reconciled = await reconcileExecutorEmailEnv({
      credential,
      projectEndpoint: project.endpoint,
      agentName,
      feature,
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          email: enable ? "on" : "off",
          resourceGroup,
          acsEndpoint: feature.acsEndpoint,
          acsSender: feature.acsSender,
          executor: agentName,
          executorNameProvenance: provenance,
          executorReconciled: reconciled.changed,
          executorAbsent: reconciled.absent,
          ...(reconciled.unreadable !== undefined ? { executorUnreadable: reconciled.unreadable } : {}),
          ...(reconciled.version !== undefined ? { executorVersion: reconciled.version } : {}),
        }) + "\n",
      );
      return reconciled.unreadable === undefined ? 0 : 1;
    }

    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "outbound email", value: enable ? "on" : "off" },
        { key: "resource group", value: resourceGroup },
        { key: "sender", value: feature.acsSender === "" ? "(none on record)" : feature.acsSender },
        {
          key: "executor",
          // 🔴 `unreadable` MUST be its own branch. Falling through to "already correct" would
          // have this command — the one an operator runs to FIX email — affirmatively claim
          // success while having changed nothing, which is the exact conflation of broken with
          // fine that this whole change exists to eliminate.
          value: reconciled.unreadable !== undefined
            ? `${agentName} (could NOT be read: ${reconciled.unreadable} — wiring NOT reconciled)`
            : reconciled.absent
              ? `${agentName} (not deployed — nothing to reconcile)`
              : reconciled.changed
                ? `${agentName} → version ${reconciled.version ?? "?"}`
                : `${agentName} (already correct)`,
        },
      ]) + "\n",
    );
    if (reconciled.absent) {
      this.context.stdout.write(
        `  ${colors.hint("note:")} no executor is deployed here, so nothing can send yet. The decision is recorded and will apply when one is deployed.\n`,
      );
    }
    if (provenance === "fallback" && reconciled.absent) {
      // Only for a GUESS. An operator who passed --agent already knows the name they chose;
      // repeating it back as a suggestion would send them chasing their own input.
      this.context.stdout.write(
        `  ${colors.hint("note:")} '${agentName}' is a fallback name — the executor could not be discovered by persona. If yours is named differently, re-run with --agent <name>.\n`,
      );
    }
    if (reconciled.unreadable !== undefined) {
      this.context.stdout.write(
        colors.error(
          `the decision is recorded, but ${agentName} was NOT reconciled — it exists and could not be read. ` +
            "Nothing about sending has changed yet; fix access and re-run.",
        ) + "\n",
      );
      return 1;
    }
    this.context.stdout.write(
      colors.success(enable ? "outbound email is on ✓" : "outbound email is off ✓") + "\n",
    );
    return 0;
  }

  /** The install's resource suffix, or null when it cannot be worked out.
   *
   *  Returns null rather than throwing: it is only needed if `resolveAcs` reaches its
   *  provisioning leg, and failing the whole command over a missing AZURE_KEYVAULT_URI would
   *  break the flip on every install that already has an ACS on record. */
  private resolveSuffix(): string | null {
    try {
      const explicit = typeof this.kvUri === "string" && this.kvUri.length > 0 ? this.kvUri : undefined;
      return platformSuffixFromKvUri(discoverKvUri(process.env, explicit));
    } catch {
      return null;
    }
  }
}
